/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo } from 'react';
import { PLATS, NM, COST_FIELDS } from '../utils/constants';
import { calcPlatform, calcIRR, getGameCashflows, getGameState } from '../utils/calc';
import { PLATS as PLATS_CONST } from '../utils/constants';
import { getCostState, getDealTerms } from '../utils/stateFactory';
import { fu, ml, mlSafe } from '../utils/format';

function sumField(arr) {
  return (arr || []).reduce((a, v) => a + (parseFloat(v) || 0), 0);
}

// Smart axis formatter — rounds to M or K
function fAxis(n) {
  if (n === 0) return '0';
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000)     return Math.round(n / 1_000) + 'K';
  return Math.round(n).toString();
}

function VarCell({ budget, forecast }) {
  if (budget === null || budget === undefined) return <td className="out-val">—</td>;
  if (budget === 0) {
    // No original budget — show forecast as the variance (it's all incremental)
    return <td className={`out-val ${forecast > 0 ? 'var-over' : 'var-zero'}`}>{forecast > 0 ? fu(forecast) : '—'}</td>;
  }
  const v = forecast - budget;
  return <td className={`out-val ${v > 0 ? 'var-over' : v < 0 ? 'var-under' : 'var-zero'}`}>{fu(v)}</td>;
}


// ── WISHLIST PLACEHOLDER (Pre-Launch) ────────────────────────────────────────
function WishlistPlaceholder() {
  const W = 380, H = 180, PAD_L = 36, PAD_B = 28, PAD_T = 14, PAD_R = 10;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_B - PAD_T;

  // Fake wishlist velocity data (organic growth curve)
  const fakeData = [120,145,165,200,240,320,490,460,400,390,385,375];
  const maxVal = Math.max(...fakeData);

  const TEAL = '#1a9e8a';

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {/* Chart */}
      <div style={{ flex: '1 1 340px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>Wishlist Velocity (weekly avg)</span>
          <span style={{ fontSize: 9, background: TEAL, color: '#fff', padding: '2px 8px', borderRadius: 3, letterSpacing: '.06em', textTransform: 'uppercase' }}>Coming Soon</span>
        </div>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
          {[0.25,0.5,0.75,1].map((f,i) => (
            <line key={i} x1={PAD_L} y1={PAD_T + chartH*(1-f)} x2={W-PAD_R} y2={PAD_T + chartH*(1-f)}
              stroke="var(--e0)" strokeWidth="0.5" />
          ))}
          {fakeData.map((v, idx) => {
            const bw = (chartW / fakeData.length) - 2;
            const x = PAD_L + (idx / fakeData.length) * chartW + 1;
            const barH = (v / maxVal) * chartH;
            return (
              <rect key={idx} x={x} y={PAD_T + chartH - barH} width={bw} height={barH}
                fill={TEAL} opacity="0.75" rx="1" />
            );
          })}
          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, idx) => (
            <text key={idx} x={PAD_L + (idx + 0.5) * (chartW / 12)} y={H - PAD_B + 10}
              textAnchor="middle" fill="var(--t4)" fontSize="7">{m}</text>
          ))}
          <line x1={PAD_L} y1={PAD_T + chartH} x2={W-PAD_R} y2={PAD_T + chartH} stroke="var(--e1)" strokeWidth="1" />
        </svg>
        <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4 }}>
          — &nbsp; Wishlist data will appear here once connected
        </div>
      </div>

      {/* Forecast panel */}
      <div style={{ flex: '0 0 200px', background: 'var(--s1)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', padding: '14px 16px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: TEAL, marginBottom: 12 }}>Forecast</div>
        {[
          { label: 'Est. Launch Wishlists', value: '—' },
          { label: 'Wishlist Velocity',     value: '— /wk' },
          { label: 'Conv. Rate (est.)',      value: '—' },
          { label: 'Price Point',            value: '—' },
          { label: 'Est. M1 Units',          value: '—' },
          { label: 'Est. M1 Revenue',        value: '—' },
          { label: 'Proj. IRR',              value: '—' },
        ].map(row => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid var(--e0)' }}>
            <span style={{ fontSize: 10, color: 'var(--t3)' }}>{row.label}</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--t2)' }}>{row.value}</span>
          </div>
        ))}
        <div style={{ fontSize: 9, color: 'var(--t4)', marginTop: 8, fontStyle: 'italic' }}>
          Coming soon — upload wishlist data to populate
        </div>
      </div>
    </div>
  );
}

// ── SUMMARY BAR
function SummaryBar({ game, PS, CS, DT, currentMonthIdx, functionalCurrency, convert }) {
  const dt    = getDealTerms(DT, game);
  const costs = getCostState(CS, game);

  const fc = functionalCurrency || 'USD';
  const cv = (v, i) => convert ? convert(v, fc, i ?? 0) : v;

  // Original Budget — dev advance converted to FC, pub/mkt already in FC
  const devAdvRaw  = parseFloat(dt.devAdvanceAmt)  || 0;
  const devAdvFC   = devAdvRaw > 0 ? cv(devAdvRaw, currentMonthIdx ?? 0) : 0;
  const origBudget = devAdvFC
    + (parseFloat(dt.pubCostsBudget)  || 0)
    + (parseFloat(dt.marketingBudget) || 0);

  let latestBudget = 0;
  ['recoup','nonRecoup'].forEach(sec => {
    COST_FIELDS.forEach(({ key }) => {
      (costs[sec][key] || []).forEach(v => { latestBudget += parseFloat(v) || 0; });
    });
  });

  const budgetDiff = latestBudget - origBudget;

  // Spent to date and this month — cost arrays use PRE_MONTHS offset
  // Array index = monthIdx + PRE_MONTHS, so forecast month 0 = array index 36
  const effectiveIdx = currentMonthIdx ?? NM - 1;
  let spendToDate = 0, spendThisMonth = 0;
  ['recoup','nonRecoup'].forEach(sec => {
    COST_FIELDS.forEach(({ key }) => {
      (costs[sec][key] || []).forEach((v, arrayIdx) => {
        const n = parseFloat(v) || 0;
        if (!n) return;
        const monthIdx = arrayIdx - 36; // PRE_MONTHS = 36
        if (monthIdx <= effectiveIdx) spendToDate += n;
        if (monthIdx === effectiveIdx) spendThisMonth += n;
      });
    });
  });

  // Revenue to date and this month — net revenue converted to FC
  let revenueToDate = 0, revenueThisMonth = 0;
  PLATS.forEach(p => {
    const st = PS[game]?.[p.key];
    if (!st?.enabled) return;
    calcPlatform(p.key, st).netRaw.forEach((v, i) => {
      if (v === null) return;
      const vFC = convert ? convert(v, fc, i) : v;
      if (i <= effectiveIdx) revenueToDate += vFC;
      if (i === effectiveIdx) revenueThisMonth += vFC;
    });
  });

  // IRR
  const cashflows = getGameCashflows(PS, CS, DT, game, PLATS);
  // Find first month with data
  const firstIdx = cashflows.findIndex(v => v !== 0);
  const irrCashflows = firstIdx >= 0 ? cashflows.slice(firstIdx) : cashflows;
  const irr = calcIRR(irrCashflows);
  const irrAnnual = irr !== null ? Math.pow(1 + irr, 12) - 1 : null;

  // origBudget already in FC, latestBudget already in FC
  const forecastDiff = latestBudget - origBudget;

  const metrics = [
    { label: 'Original Budget',    value: origBudget > 0 ? fu(origBudget) : '—', sub: null },
    { label: 'Current Forecast',   value: fu(latestBudget),
      sub: origBudget > 0 && forecastDiff !== 0
        ? { val: fu(Math.abs(forecastDiff)), positive: forecastDiff < 0, label: forecastDiff > 0 ? '▲ vs budget' : '▼ vs budget' }
        : null },
    { label: currentMonthIdx !== null ? 'Spent to Date' : 'Total Forecast Spend', value: fu(spendToDate), sub: null },
    { label: currentMonthIdx !== null ? 'Revenue to Date' : 'Total Forecast Revenue', value: fu(revenueToDate), sub: null },
    { label: currentMonthIdx !== null ? 'Spent This Month' : '—', value: currentMonthIdx !== null ? fu(spendThisMonth) : '—', sub: null },
    { label: currentMonthIdx !== null ? 'Revenue This Month' : '—', value: currentMonthIdx !== null ? fu(revenueThisMonth) : '—', sub: null },
    { label: 'Project IRR (Annual)', value: irrAnnual !== null ? Math.round(irrAnnual * 100) + '%' : '—', sub: null, highlight: irrAnnual !== null },
  ];

  return (
    <div className="out-summary-bar">
      {metrics.map((m, i) => (
        <div key={i} className="out-metric">
          <div className="out-metric-label">{m.label}</div>
          <div className={`out-metric-value ${m.highlight ? (irrAnnual >= 0 ? 'var-under' : 'var-over') : ''}`}>{m.value}</div>
          {m.sub && (
            <div className={`out-metric-sub ${m.sub.positive ? 'var-under' : 'var-over'}`}>
              {m.sub.label} {m.sub.val}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── COST SUMMARY TABLE
function CostSummaryTable({ game, CS, DT, functionalCurrency, convert, currentMonthIdx }) {
  const costs = getCostState(CS, game);
  const dt    = getDealTerms(DT, game);
  const fc    = functionalCurrency || 'USD';
  const cv    = (v) => convert && fc !== 'USD' ? convert(v, fc, currentMonthIdx ?? 0) : v;

  // Recoupable
  const recDevAdv   = sumField(costs.recoup.devAdvances);
  const recPubCosts = sumField(costs.recoup.loc) + sumField(costs.recoup.qa) + sumField(costs.recoup.otherPub);
  const recMkt      = sumField(costs.recoup.marketing);
  const totalRec    = recDevAdv + recPubCosts + recMkt;

  // Non-recoupable — no breakdown needed
  const nrDevAdv    = sumField(costs.nonRecoup.devAdvances);
  const nrPubCosts  = sumField(costs.nonRecoup.loc) + sumField(costs.nonRecoup.qa) + sumField(costs.nonRecoup.otherPub);
  const nrMkt       = sumField(costs.nonRecoup.marketing);
  const totalNR     = nrDevAdv + nrPubCosts + nrMkt;

  // Original Budget from Deal Terms — dev advance converted to FC, pub/mkt already FC
  const budgetDevAdv = parseFloat(dt.devAdvanceAmt)  ? cv(parseFloat(dt.devAdvanceAmt)) : null;
  const budgetPub    = parseFloat(dt.pubCostsBudget)  ? parseFloat(dt.pubCostsBudget)   : null;
  const budgetMkt    = parseFloat(dt.marketingBudget) ? parseFloat(dt.marketingBudget)  : null;
  const budgetTotalRec = (budgetDevAdv || 0) + (budgetPub || 0) + (budgetMkt || 0);

  function Row({ label, budget, forecast }) {
    return (
      <tr className="out-row">
        <td className="out-lbl">{label}</td>
        <td className="out-val">{budget !== null && budget !== undefined ? fu(budget) : '—'}</td>
        <td className="out-val">{fu(forecast)}</td>
        <VarCell budget={budget} forecast={forecast} />
      </tr>
    );
  }

  function TotalRow({ label, budget, forecast }) {
    return (
      <tr className="out-total">
        <td className="out-lbl">{label}</td>
        <td className="out-val" style={{ fontWeight: 600 }}>{budget !== null && budget !== undefined && budget > 0 ? fu(budget) : '—'}</td>
        <td className="out-val" style={{ fontWeight: 600 }}>{fu(forecast)}</td>
        <VarCell budget={budget > 0 ? budget : null} forecast={forecast} />
      </tr>
    );
  }

  return (
    <div className="out-section">
      <table className="out-table">
        <thead>
          <tr>
            <th className="out-th-lbl"></th>
            <th className="out-th">Original Budget</th>
            <th className="out-th">Forecast</th>
            <th className="out-th">Variance</th>
          </tr>
        </thead>
        <tbody>
          <tr className="out-group-hd"><td colSpan={4}>Recoupable Costs</td></tr>
          <Row label="Development Advances" budget={budgetDevAdv} forecast={recDevAdv} />
          <Row label="Publishing Costs"     budget={budgetPub}    forecast={recPubCosts} />
          <Row label="Marketing"            budget={budgetMkt}    forecast={recMkt} />
          <TotalRow label="Total Recoupable" budget={budgetTotalRec} forecast={totalRec} />

          <tr className="out-spacer"><td colSpan={4} /></tr>

          <tr className="out-group-hd"><td colSpan={4}>Non-Recoupable Costs</td></tr>
          <Row label="Development Advances" budget={nrDevAdv > 0 ? 0 : null} forecast={nrDevAdv} />
          <Row label="Publishing Costs"     budget={nrPubCosts > 0 ? 0 : null} forecast={nrPubCosts} />
          <Row label="Marketing"            budget={nrMkt > 0 ? 0 : null} forecast={nrMkt} />
          <TotalRow label="Total Non-Recoupable" budget={totalNR > 0 ? 0 : null} forecast={totalNR} />

          <tr className="out-spacer"><td colSpan={4} /></tr>

          <tr className="out-grand">
            <td className="out-lbl">Grand Total</td>
            <td className="out-val" style={{ fontWeight: 700 }}>{budgetTotalRec > 0 ? fu(budgetTotalRec) : '—'}</td>
            <td className="out-val" style={{ fontWeight: 700 }}>{fu(totalRec + totalNR)}</td>
            <VarCell budget={budgetTotalRec > 0 ? budgetTotalRec : null} forecast={totalRec + totalNR} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── REVENUE CHART
function RevenueChart({ game, PS, startDate, currentMonthIdx }) {
  const enabledPlats = PLATS.filter(p => PS[game]?.[p.key]?.enabled);

  const PLAT_COLORS = {
    steam: '#3d8bff', psn: '#00c47a', xbox: '#68b0ff',
    nintendo: '#ff6b6b', epic: '#a78bfa', other: '#505a70',
  };

  // Find earliest launch date across all enabled platforms
  let firstLaunch = null;
  enabledPlats.forEach(p => {
    const st = PS[game][p.key];
    const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
    const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
    const earliest = ea !== null && fl !== null ? Math.min(ea, fl) : ea ?? fl;
    if (earliest !== null && (firstLaunch === null || earliest < firstLaunch)) firstLaunch = earliest;
  });
  const chartStartIdx = Math.max(0, firstLaunch ?? 0);

  const monthly = useMemo(() => {
    return Array.from({ length: NM - chartStartIdx }, (_, idx) => {
      const i = chartStartIdx + idx;
      const entry = { month: mlSafe(i, startDate), monthIdx: i, total: 0, segments: [] };
      enabledPlats.forEach(p => {
        const rc = calcPlatform(p.key, PS[game][p.key]);
        const v = rc.grossRaw[i] ?? 0;
        if (v > 0) {
          entry.segments.push({ key: p.key, label: p.label, value: v, color: PLAT_COLORS[p.key] });
          entry.total += v;
        }
      });
      return entry;
    });
  }, [game, PS, enabledPlats, startDate, chartStartIdx]);

  const maxVal = Math.max(...monthly.map(m => m.total), 1);
  const W = 460, H = 200, PAD_L = 52, PAD_B = 36, PAD_T = 10, PAD_R = 8;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_B - PAD_T;
  const barW   = Math.max(2, (chartW / NM) - 1.2);
  const yTicks  = [0, 0.25, 0.5, 0.75, 1];
  const xLabels = monthly.map((m, idx) => ({ idx, i: m.monthIdx, month: m.month })).filter((_, idx) => idx % 6 === 0);

  if (!enabledPlats.length) {
    return <div className="out-chart-empty"><p className="nomsg">Enable platforms in Game Inputs to see revenue chart.</p></div>;
  }

  return (
    <div className="out-chart-wrap">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="out-chart-svg">
        {yTicks.map((f, i) => {
          const y = PAD_T + chartH * (1 - f);
          return (
            <g key={i}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="var(--e0)" strokeWidth="1" />
              {i > 0 && (
                <text x={PAD_L - 4} y={y + 3.5} textAnchor="end"
                  fill="var(--t4)" fontSize="6" fontFamily="var(--sans)">
                  {fAxis(maxVal * f)}
                </text>
              )}
            </g>
          );
        })}
        {monthly.map((m, idx) => {
          const i = m.monthIdx;
          const x = PAD_L + (idx / monthly.length) * chartW + 0.75;
          const bw = Math.max(2, (chartW / monthly.length) - 1.2);
          let yOffset = PAD_T + chartH;
          return (
            <g key={i}>
              {m.segments.map(seg => {
                const barH = (seg.value / maxVal) * chartH;
                yOffset -= barH;
                return (
                  <rect key={seg.key} x={x} y={yOffset} width={bw} height={barH}
                    fill={seg.color} 
                    opacity={currentMonthIdx !== null && i > currentMonthIdx ? "0.35" : "0.85"}>
                    <title>{seg.label}: {fu(seg.value)}{currentMonthIdx !== null && i > currentMonthIdx ? ' (forecast)' : ''}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
        {xLabels.map(({ idx, i, month }) => (
          <text key={i}
            x={PAD_L + (idx / monthly.length) * chartW + barW / 2}
            y={H - PAD_B + 12}
            textAnchor="middle" fill="var(--t4)" fontSize="6" fontFamily="var(--sans)">
            {month}
          </text>
        ))}
        <line x1={PAD_L} y1={PAD_T + chartH} x2={W - PAD_R} y2={PAD_T + chartH}
          stroke="var(--e1)" strokeWidth="1" />
      </svg>
      <div className="out-legend">
        {enabledPlats.map(p => (
          <span key={p.key} className="out-legend-item">
            <span className="out-legend-pip" style={{ background: PLAT_COLORS[p.key] }} />
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const STATE_STYLE = {
  'Pre-Launch': { color: 'var(--amber)', bg: 'rgba(245,166,35,.1)', border: 'rgba(245,166,35,.3)' },
  'Pre-Recoup': { color: 'var(--blue)',  bg: 'var(--blue-dim)',     border: 'rgba(77,159,255,.3)' },
  'Recouped':   { color: 'var(--green)', bg: 'var(--green-dim)',    border: 'rgba(0,217,139,.3)'  },
  'Unknown':    { color: 'var(--t3)',    bg: 'var(--s2)',           border: 'var(--e1)'           },
};

// ── MAIN PANEL
export default function GameOutputPanel({ games, PS, CS, DT, startDate = null, currentMonthIdx = null, functionalCurrency, convert, convertFrom, defaultGame = '' }) {
  const [curGame, setCurGame] = useState(defaultGame || games[0] || '');

  const gameState = curGame ? getGameState(PS, CS, DT, curGame, PLATS_CONST, currentMonthIdx) : null;
  const stateStyle = gameState ? (STATE_STYLE[gameState] || STATE_STYLE.Unknown) : null;

  return (
    <div className="panel">
      <div className="gi-bar">
        <span className="gi-lbl">Game</span>
        <select value={curGame} onChange={e => setCurGame(e.target.value)} style={{ minWidth: 190 }}>
          <option value="">— select game —</option>
          {games.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        {curGame && gameState && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8,
            fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
            padding: '4px 12px', borderRadius: 'var(--r)',
            color: stateStyle.color, background: stateStyle.bg, border: `1px solid ${stateStyle.border}`,
          }}>
            {gameState}
          </span>
        )}
      </div>

      {!curGame && <p className="nomsg">Select a game above to view outputs.</p>}

      {curGame && (
        <>
          <SummaryBar game={curGame} PS={PS} CS={CS} DT={DT} currentMonthIdx={currentMonthIdx} functionalCurrency={functionalCurrency} convert={convert} />
          <div className="out-layout">
            <div className="out-left">
              <div className="card">
                <div className="card-head">
                  <span className="card-title">Cost Summary</span>
                  <span className="card-meta">{curGame}</span>
                </div>
                <div className="card-body" style={{ padding: '12px 16px' }}>
                  <CostSummaryTable game={curGame} CS={CS} DT={DT} functionalCurrency={functionalCurrency} convert={convert} currentMonthIdx={currentMonthIdx} />
                </div>
              </div>
            </div>
            <div className="out-right">
              <div className="card">
                <div className="card-head">
                  <span className="card-title">{gameState === 'Pre-Launch' ? 'Wishlists' : 'Gross Revenue by Platform'}</span>
                  <span className="card-meta">{gameState === 'Pre-Launch' ? 'Pre-Launch' : '36-Month Forecast'}</span>
                </div>
                <div className="card-body">
                  {gameState === 'Pre-Launch'
                    ? <WishlistPlaceholder />
                    : <RevenueChart game={curGame} PS={PS} startDate={startDate} currentMonthIdx={currentMonthIdx} />
                  }
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
